<template>
  <q-list dense>
    <q-item-wrapper
      v-if="modelValue.length < 1"
      dense
      class="no-padding"
      :class="{'q-mb-sm': canAddValue}"
    >
      <q-field
        outlined
        dense
        readonly
        hide-bottom-space
        :error="error"
      >
        <template #control>
          <span class="text-orange-10">
            {{ nullLabel }}
          </span>
        </template>
      </q-field>
    </q-item-wrapper>

    <q-item-wrapper
      v-for="(offset, key) in modelValue"
      :key="key"
      class="no-padding q-mb-sm"
      dense
    >
      <minute-picker
        ref="picker"
        :model-value="offset"
        :disable="disable"
        :error="errorAtIndex === key"
        dense
        v-bind="minutePickerProps"
        @update:model-value="updateIndexValue(key, $event)"
      />

      <template v-if="!disable" #after>
        <q-item-section side>
          <cancel-button flat @click="removeIndex(key)" />
        </q-item-section>
      </template>
    </q-item-wrapper>

    <div
      v-if="!!error"
      class="text-red q-pa-sm bg-white"
    >
      <q-icon name="mdi-exclamation" size="1.5em" />
      {{ errorMessage }}
    </div>

    <q-btn
      v-if="canAddValue"
      size="12px"
      icon="mdi-plus"
      :color="error ? 'red-7' : 'blue-7'"
      :label="addLabel"
      @click="addIndex"
    />
  </q-list>
</template>

<script>
/**
 * EditableOffsetList accepts an array of numbers to parse as a list of simple reminders before/after
 * Does NOT accept full reminder objects
 */
export default {
  props: {
    // Array of offsets
    modelValue: {
      type: Array,
      required: true
    },
    // Limit amount of new options to add
    limit: {
      type: Number,
      default: null
    },
    // Disable input
    disable: {
      type: Boolean,
      default: false
    },
    // Value to display if array is empty
    nullLabel: {
      type: String,
      default: 'None'
    },
    // Value to display in button for adding a new value
    addLabel: {
      type: String,
      default: 'Add Value'
    },
    // Default offset when adding 0th value to array
    defaultOffset: {
      type: Number,
      default: 0
    },
    // Value to increment by when adding additional values to array
    incrementOffset: {
      type: Number,
      default: 0
    },
    // Any props to specify for restricting values on the MinutePicker
    minutePickerProps: {
      type: Object,
      default: () => {
        return {}
      }
    },
    error: {
      type: Boolean,
      default: false
    },
    // Highlight one MinutePicker object at index
    errorAtIndex: {
      type: Number,
      default: -1
    },
    errorMessage: {
      type: String,
      default: null
    }
  },
  emits: ['update:modelValue'],
  computed: {
    canAddValue () {
      return !this.disable && (!this.limit || this.modelValue.length < this.limit)
    }
  },
  methods: {
    addIndex () {
      let newOffset = this.defaultOffset
      const sortArr = [...this.modelValue].sort((a, b) => a - b)
      if (sortArr.length > 0) {
        const index = this.incrementOffset >= 0 ? sortArr.length - 1 : 0
        newOffset = (sortArr[index] ?? 0) + this.incrementOffset
      }

      const arr = [...this.modelValue]
      arr.push(newOffset)
      this.$emit('update:modelValue', arr)

      // If adding nw value to array, attempt to focus on that picker
      this.timed(() => {
        if ((this.$refs.picker || []).length > 0) {
          this.$refs.picker[this.$refs.picker.length - 1].focus()
        }
      }, 100)
    },
    updateIndexValue (key, value) {
      const arr = [...this.modelValue]
      arr[key] = value
      this.$emit('update:modelValue', arr)
    },
    removeIndex (key) {
      const arr = [...this.modelValue]
      arr.splice(key, 1)
      this.$emit('update:modelValue', arr)
    }
  }
}
</script>
